import { Controller, useFormContext } from "react-hook-form";

import { ErrorHint } from "src/base-components/ErrorHint";
import { FormItem } from "src/base-components/FormItem";
import { Input } from "src/base-components/Input";
import { Switch } from "src/base-components/Switch";
import { ConfigFieldsetCard } from "src/connections/config/FieldsetCard";
import { isFieldErrored } from "src/connections/config/isFieldErrored";
import {
  CockroachDBConnectionConfigInputs,
  Environment,
} from "src/connections/types";

type Props = {
  environment: Environment;
};

export const CockroachDBRetryConfigFields: React.FC<Props> = ({
  environment,
}) => {
  const {
    control,
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<CockroachDBConnectionConfigInputs>();
  const envPrefix = `${environment}Config` as const;
  const hasRetryEnabledFieldName = `${envPrefix}.hasRetryEnabled` as const;
  const retryConfigFieldName = `${envPrefix}.retryConfig` as const;
  const isRetryEnabled = Boolean(watch(hasRetryEnabledFieldName));
  const retryConfig = watch(retryConfigFieldName);
  const retryConfigErrors = errors[envPrefix]?.retryConfig;

  return (
    <div className="mb-6 last:mb-0">
      <FormItem
        className="flex flex-row items-center justify-between"
        description="Enable retries to handle concurrent transaction errors automatically"
        gap="xxs"
        helpTooltipBody={
          <span>
            CockroachDB is designed without traditional locking: multiple
            instances can access and modify the same data at the same time. When
            this happens, CockroachDB will frequently return a transaction retry
            error (<code>40001</code>). This is by design and is a common
            scenario—not a sign of a misconfigured database. Enabling automatic
            retries allows your application to transparently handle these errors
            by re-running failed transactions as recommended. We strongly
            encourage enabling this option to handle transaction retries
            efficiently in production environments.
          </span>
        }
        label="Retry transactions automatically"
      >
        <Controller
          control={control}
          defaultValue={false}
          name={hasRetryEnabledFieldName}
          render={(props) => (
            <Switch
              dataLoc={`${environment}-cockroachdb-connection-retry-active`}
              enabled={Boolean(props.field.value)}
              onChange={() => {
                const isEnabling = !props.field.value;
                props.field.onChange(isEnabling);

                if (isEnabling) {
                  setValue(retryConfigFieldName, {
                    maxRetries: retryConfig?.maxRetries ?? 3,
                    maxBackoff: retryConfig?.maxBackoff,
                  });
                }
              }}
            />
          )}
        />
      </FormItem>
      {isRetryEnabled && (
        <ConfigFieldsetCard>
          <div>
            <FormItem
              description="Maximum number of retry attempts"
              label="Max retries"
              isRequired
            >
              {isFieldErrored(retryConfigErrors, "maxRetries") && (
                <ErrorHint>{retryConfigErrors?.maxRetries?.message}</ErrorHint>
              )}
              <Input
                data-loc={`${environment}-cockroachdb-connection-retry-max-retries`}
                errored={Boolean(retryConfigErrors?.maxRetries)}
                placeholder="e.g. 3"
                type="number"
                fullWidth
                {...register(`${retryConfigFieldName}.maxRetries`, {
                  valueAsNumber: true,
                  required: "Max retries is required",
                  min: {
                    value: 1,
                    message: "Max retries must be at least 1",
                  },
                })}
              />
            </FormItem>
            <FormItem
              description="Maximum backoff between retries in seconds"
              helpTooltipBody={
                <span>
                  Sets the upper limit (in seconds) for how long CockroachDB
                  will wait between retry attempts using exponential backoff
                  after a failed query. After each failure, the wait time
                  doubles until it reaches this cap, helping to avoid
                  overwhelming the database with rapid retries.
                </span>
              }
              label="Max backoff (seconds)"
            >
              {isFieldErrored(retryConfigErrors, "maxBackoff") && (
                <ErrorHint>{retryConfigErrors?.maxBackoff?.message}</ErrorHint>
              )}
              <Input
                data-loc={`${environment}-cockroachdb-connection-retry-max-backoff`}
                errored={Boolean(retryConfigErrors?.maxBackoff)}
                placeholder="e.g. 10"
                type="number"
                fullWidth
                {...register(`${retryConfigFieldName}.maxBackoff`, {
                  setValueAs: (value) => {
                    if (value === "" || value === undefined) {
                      return undefined;
                    }
                    return Number(value);
                  },
                  min: {
                    value: 0,
                    message: "Max backoff must be at least 0",
                  },
                })}
              />
            </FormItem>
          </div>
        </ConfigFieldsetCard>
      )}
    </div>
  );
};
